import { useUrlFilters } from "@/hooks/use-url-filters";
import { mapDefaults } from "@/info/map-defaults";
import { useSearchPageMapData } from "@/providers/search";
import { useEffect, useRef } from "react";

interface UseMapUrlSyncProps {
	backendMapCenter?: { lat: number; lng: number } | null;
	coordinates?: { lat: number; lng: number } | null;
}

/**
 * Simplified hook for map-URL synchronization.
 * Since URL is now the single source of truth, this hook only handles:
 * 1. Initial coordinate setup from backend data
 * 2. Zoom synchronization from URL to map state
 */
export function useMapUrlSync({ backendMapCenter, coordinates }: UseMapUrlSyncProps) {
	const { filters, setFilters } = useUrlFilters();
	const setMapZoom = useSearchPageMapData(state => state.setMapZoom);
	const hasInitializedRef = useRef(false);

	// Initialize coordinates from backend data if not in URL
	useEffect(() => {
		if (hasInitializedRef.current) return;
		hasInitializedRef.current = true;

		const updatedFilters = { ...filters };
		let hasChanges = false;

		// Set default zoom if not present
		if (filters.zoom === undefined) {
			updatedFilters.zoom = mapDefaults.zoom;
			hasChanges = true;
		}

		// Set coordinates from backend data if not in URL
		if (!coordinates && !filters.coordinates && backendMapCenter) {
			updatedFilters.coordinates = {
				lat: parseFloat(backendMapCenter.lat.toFixed(6)),
				lng: parseFloat(backendMapCenter.lng.toFixed(6)),
			};
			hasChanges = true;
		}

		if (hasChanges) {
			setFilters(updatedFilters, { useReplaceState: true });
		}
	}, [backendMapCenter, coordinates, filters, setFilters]);

	// Sync zoom from URL to map state
	useEffect(() => {
		if (filters.zoom !== undefined) {
			setMapZoom(filters.zoom);
		}
	}, [filters.zoom, setMapZoom]);

	return {
		// Update URL coordinates when map center changes (used by nav-overlay)
		updateUrlCoordinates: (newCenter: { lat: number; lng: number }) => {
			const roundedCenter = {
				lat: parseFloat(newCenter.lat.toFixed(6)),
				lng: parseFloat(newCenter.lng.toFixed(6)),
			};

			setFilters(
				{
					...filters,
					coordinates: roundedCenter,
				},
				{ useReplaceState: true },
			);
		},
	};
}
